import { useMemo } from "react";
import { useCalendar } from "../../../../context/CalendarContext";
import { CalendarEventType } from "../../../../types/calendar";
import { GetTransactionsResponse } from "../../../../types/transactions";
import {
  eventStaffIn,
  passesCalendarScope,
  passesUserFilters,
} from "./eventFilters";

interface UseFilteredEventsArgs {
  events: CalendarEventType[];
  transactions: GetTransactionsResponse[];
  calendarScope: string;
  currentUserId: string;
}

/**
 * Events visible under the active staff, Filters dropdown, and calendar.scope.
 * `scopedEvents` applies only calendar.scope (the base the mobile Filters
 * sheet counts a pending selection against).
 */
export function useFilteredEvents({
  events,
  transactions,
  calendarScope,
  currentUserId,
}: UseFilteredEventsArgs) {
  const {
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
    filteredEmployees,
    workingToday,
    workingStaffIds,
  } = useCalendar();

  // Create set of active staff IDs for efficient lookup
  const activeStaffIds = useMemo(() => {
    return new Set(filteredEmployees.map((emp) => emp.id));
  }, [filteredEmployees]);

  // calendar.scope permission (UI-only filtering): "own" = only the user's
  // own appointments/time blocks, "all" = everything.
  const scopedEvents = useMemo(
    () =>
      events.filter((event) =>
        passesCalendarScope(event, calendarScope, currentUserId, transactions),
      ),
    [events, calendarScope, currentUserId, transactions],
  );

  // Filter events based on selected filters and permission scope
  const filteredEvents = useMemo(() => {
    return scopedEvents.filter((event) => {
      // Filter out events from inactive staff members
      if (!eventStaffIn(event, activeStaffIds)) return false;

      // "Working today": also require the event's staff to be working on the
      // viewed date (applies to both day and week view).
      if (workingToday && !event.isTimeBlock) {
        if (!eventStaffIn(event, workingStaffIds)) return false;
      }

      return passesUserFilters(event, {
        selectedArtists,
        selectedServiceTypes,
        selectedStatuses,
      });
    });
  }, [
    scopedEvents,
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
    activeStaffIds,
    workingToday,
    workingStaffIds,
  ]);

  return { activeStaffIds, filteredEvents, scopedEvents };
}
